// The one place the Next Steps encryption modules reach for the platform's
// WebCrypto. Everything else imports from here so that a runtime without it
// (an old WebView, a locked-down browser) fails with one named error at the
// first call instead of a TypeError somewhere inside a derivation.
import { bytesToBase64Url, bytesToHex, utf8Encode } from './encoding';

/**
 * Every HKDF info string this app derives under. FROZEN: four unrelated
 * derivations share it (per-item keys, device wrappings, the recovery KEK, the
 * tag blind index) and only the first is versioned by the envelope's `v`. A
 * new format changes its own info suffix, never this prefix; changing it would
 * orphan every stored wrapping and every tag index with nothing to notice.
 */
export const HKDF_INFO_PREFIX = 'hogc-one/courses/v1/';

export class CryptoUnavailableError extends Error {
  constructor() {
    super('WebCrypto is not available in this runtime');
    this.name = 'CryptoUnavailableError';
  }
}

/**
 * @returns {Crypto}
 */
function getCrypto() {
  const crypto = globalThis.crypto;
  if (!crypto || !crypto.subtle || typeof crypto.getRandomValues !== 'function') {
    throw new CryptoUnavailableError();
  }
  return crypto;
}

/**
 * @returns {SubtleCrypto}
 */
export function getSubtle() {
  return getCrypto().subtle;
}

/**
 * @param {number} length
 * @returns {Uint8Array}
 */
export function randomBytes(length) {
  if (!Number.isInteger(length) || length <= 0) {
    throw new RangeError('randomBytes: length must be a positive integer');
  }
  return getCrypto().getRandomValues(new Uint8Array(length));
}

/**
 * A v4 UUID. `crypto.randomUUID` arrived in WebKit 15.4 and Chromium 92; older
 * WebViews get the same shape built from `getRandomValues`.
 *
 * @returns {string}
 */
export function randomUuid() {
  const crypto = getCrypto();
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = randomBytes(16);
  const versioned = Uint8Array.from(bytes);
  versioned[6] = (versioned[6] & 0x0f) | 0x40;
  versioned[8] = (versioned[8] & 0x3f) | 0x80;
  const hex = bytesToHex(versioned);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * @param {Uint8Array} bytes
 * @returns {Promise<Uint8Array>}
 */
export async function sha256(bytes) {
  return new Uint8Array(await getSubtle().digest('SHA-256', bytes));
}

/**
 * @param {Uint8Array} bytes
 * @returns {Promise<string>} base64url SHA-256, the shape of every hash the API compares
 */
export async function sha256Base64Url(bytes) {
  return bytesToBase64Url(await sha256(bytes));
}

/**
 * HKDF-SHA256 from raw input keying material to a WebCrypto key. `info` is
 * given without the prefix; the versioned prefix is applied here so no caller
 * can derive outside the app's namespace.
 *
 * @param {object} args
 * @param {Uint8Array} args.ikm
 * @param {Uint8Array} args.salt
 * @param {string} args.info suffix after HKDF_INFO_PREFIX
 * @param {AesKeyGenParams | HmacKeyGenParams} args.algorithm the derived key's algorithm
 * @param {KeyUsage[]} args.usages
 * @param {boolean} [args.extractable=false]
 * @returns {Promise<CryptoKey>}
 */
export async function hkdfKey({ ikm, salt, info, algorithm, usages, extractable = false }) {
  const subtle = getSubtle();
  const base = await subtle.importKey('raw', ikm, 'HKDF', false, ['deriveKey']);
  return subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: utf8Encode(HKDF_INFO_PREFIX + info) },
    base,
    algorithm,
    extractable,
    usages,
  );
}
